import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import * as types from "types";
import { deleteFile } from "services/fileApi/methods/deleteFile";
import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import { SCOPE_FILES_QUERY_KEY } from "services/fileApi/hooks/scopeFilesKeys";
import useGetAccessToken from "components/Login/useGetAccessToken";
import useRemoveMessagesAfterStream from "services/dbApi/hooks/useRemoveMessagesAfterStream";
import useMessagesQuery from "services/dbApi/hooks/useMessagesQuery";
import { useIsThreadStreaming } from "services/cortevaChain/hooks/activeStreams";
import { PRODUCT_NAME } from "utilities/productName";

interface UseDeleteThreadFilesArgs {
  /** Undefined on the new-chat route and while the threads list loads. */
  chatThread: types.ChatThread | undefined;
}

/**
 * Deletes files from the File API, then removes their file messages from the
 * thread in one messages PUT (ADR 071).
 *
 * If the PUT fails, `useCommitMessages` throws a `MessagesWriteError` while the
 * component that called this hook renders. Call it from a component that stays
 * mounted until the PUT finishes, such as `FileExplorer`. Deleting the last
 * file unmounts `FileList`, so a failure would never show if it were called
 * there.
 */
export default function useDeleteThreadFiles({
  chatThread,
}: UseDeleteThreadFilesArgs) {
  const queryClient = useQueryClient();
  const threadId = chatThread?.id;
  const messagesQuery = useMessagesQuery({
    // `useMessagesQuery` needs a string ID. Without a thread, pass "" and
    // disable the query, so the empty ID is never fetched.
    threadId: threadId ?? "",
    disabled: threadId === undefined,
  });
  const removeMessagesAfterStream = useRemoveMessagesAfterStream({
    errorTitle: "Couldn't delete the files",
    errorDetail: `${PRODUCT_NAME} couldn't save your change after several attempts. Nothing already saved is affected.`,
  });
  const getAccessToken = useGetAccessToken();
  const isStreaming = useIsThreadStreaming(threadId);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Disabled until the messages load, so no file message is left pointing at a
  // deleted file, and while a reply streams or a delete runs. Reading
  // `isSuccess` rather than `data` avoids a re-render per streamed chunk.
  const canDelete = messagesQuery.isSuccess && !isDeleting && !isStreaming;

  const deleteFiles = React.useCallback(
    async (files: ScopeFile[]) => {
      if (threadId === undefined || files.length === 0) return;
      const scopeFilesKey = SCOPE_FILES_QUERY_KEY(threadId);

      let prompt = `Are you sure you want to delete ${files.length} files?`;
      if (files.length === 1) {
        prompt = `Are you sure you want to delete ${files[0].filename}?`;
      }
      if (!window.confirm(prompt)) return;

      setIsDeleting(true);
      try {
        const results = await Promise.allSettled(
          files.map((file) => deleteFile(file.fileId, getAccessToken))
        );
        const deletedFileIds = new Set<string>();
        const failures: Array<{ filename: string; reason: unknown }> = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            deletedFileIds.add(files[index].fileId);
          } else {
            failures.push({
              filename: files[index].filename,
              reason: result.reason,
            });
          }
        });
        if (failures.length > 0) {
          console.error("Couldn't delete files", failures);
        }
        if (deletedFileIds.size === 0) {
          // A delete also fails when the file is already gone, so refetch.
          void queryClient.invalidateQueries({ queryKey: scopeFilesKey });
          return;
        }

        // Drop the files from the list now. A refetch in flight would bring
        // them back, so cancel it.
        await queryClient.cancelQueries({ queryKey: scopeFilesKey });
        queryClient.setQueryData<ScopeFile[]>(scopeFilesKey, (prev) =>
          prev?.filter((file) => !deletedFileIds.has(file.fileId))
        );

        const hasRemovedMessages = await removeMessagesAfterStream(
          threadId,
          (message) => {
            if (!types.isFileMessage(message)) return false;
            const fileId = message.meta?.file_id;
            return fileId !== undefined && deletedFileIds.has(fileId);
          }
        );
        // A successful messages PUT refetches the file list. No file messages
        // matched, so no PUT ran: refetch here instead.
        if (!hasRemovedMessages) {
          void queryClient.invalidateQueries({ queryKey: scopeFilesKey });
        }
      } finally {
        setIsDeleting(false);
      }
    },
    [threadId, getAccessToken, removeMessagesAfterStream, queryClient]
  );

  return { deleteFiles, canDelete };
}
